/**
 * ManualCredentialPasteModal — inline manual API key paste for the creation flow.
 *
 * CEO 2026-04-28: keep users on /dashboards/new; no navigation to the credentials
 * page. Opens when the user clicks "Paste an API Key you already have" in
 * CredentialPickerHarvestBlock (the TODO stub is now live).
 *
 * Visual treatment matches HarvestModal chrome (Dialog primitives, same header logo
 * treatment, same close button per spec).
 *
 * Fields from SDK apiKeyRequiredCredentials.unauthenticatedFields:
 *   - apiKey (required)
 *   - subscriptionKey → subscriptionKeyPrimary (required)
 *   - subscriptionKeySecondary (optional)
 *   - clientId (optional)
 *   - expireDate (optional, defaults to +1 year from today)
 *
 * On submit: dispatches createManualCredentialThunk → POST /v1/credentials →
 *   prefetchAvailableCredentialsForApp → picker re-renders with new credential.
 */

import { useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { ChevronRight, ShieldCheck, X } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { DatePicker } from '@/components/ui/date-picker-input'
import type { AppDispatch, RootState } from '@/redux'
import { createManualCredentialThunk } from '@/services/DashboardChatService'

// ─── Helpers ────────────────────────────────────────────────────────────────────

function addOneYear(date: Date): Date {
  const d = new Date(date)
  d.setFullYear(d.getFullYear() + 1)
  return d
}

function generateNickname(): string {
  const today = new Date()
  const yyyy = today.getFullYear()
  const mm = String(today.getMonth() + 1).padStart(2, '0')
  const dd = String(today.getDate()).padStart(2, '0')
  return `CampMinder API Key (manual entry, ${yyyy}-${mm}-${dd})`
}

// ─── FieldRow — label + input + optional error ───────────────────────────────

interface FieldRowProps {
  id: string
  label: string
  description?: string
  placeholder?: string
  value: string
  onChange: (value: string) => void
  error?: string
  required?: boolean
}

function FieldRow({
  id,
  label,
  description,
  placeholder,
  value,
  onChange,
  error,
  required,
}: FieldRowProps) {
  return (
    <div className="flex flex-col gap-1">
      <Label
        htmlFor={id}
        className="text-sm font-semibold text-primary"
      >
        {label}
        {required && (
          <span className="ml-1 text-destructive text-xs" aria-hidden="true">*</span>
        )}
      </Label>
      {description && (
        <p className="text-xs text-muted-foreground leading-relaxed">{description}</p>
      )}
      <Input
        id={id}
        value={value}
        onChange={e => onChange(e.target.value)}
        placeholder={placeholder}
        aria-required={required}
        aria-describedby={error ? `${id}-error` : undefined}
        aria-invalid={!!error}
        className={error ? 'border-destructive focus-visible:ring-destructive' : ''}
      />
      {error && (
        <p
          id={`${id}-error`}
          role="alert"
          className="text-xs text-destructive font-medium"
        >
          {error}
        </p>
      )}
    </div>
  )
}

// ─── ManualCredentialPasteModal ──────────────────────────────────────────────

interface ManualCredentialPasteModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onSuccess: () => void
}

interface FormState {
  apiKey: string
  subscriptionKeyPrimary: string
  subscriptionKeySecondary: string
  clientId: string
}

interface FormErrors {
  apiKey?: string
  subscriptionKeyPrimary?: string
  expireDate?: string
}

export const ManualCredentialPasteModal = ({
  open,
  onOpenChange,
  onSuccess,
}: ManualCredentialPasteModalProps) => {
  const dispatch: AppDispatch = useDispatch()
  const token = useSelector((state: RootState) => state.user.token ?? '')

  const defaultExpireDate = addOneYear(new Date())

  const [form, setForm] = useState<FormState>({
    apiKey: '',
    subscriptionKeyPrimary: '',
    subscriptionKeySecondary: '',
    clientId: '',
  })
  const [expireDate, setExpireDate] = useState<Date | undefined>(defaultExpireDate)
  const [errors, setErrors] = useState<FormErrors>({})
  const [submitError, setSubmitError] = useState<string | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)

  const setField = (field: keyof FormState) => (value: string) => {
    setForm(prev => ({ ...prev, [field]: value }))
    // Clear error on change
    if (errors[field as keyof FormErrors]) {
      setErrors(prev => ({ ...prev, [field]: undefined }))
    }
  }

  const validate = (): boolean => {
    const newErrors: FormErrors = {}
    if (!form.apiKey.trim()) {
      newErrors.apiKey = 'API Key is required.'
    }
    if (!form.subscriptionKeyPrimary.trim()) {
      newErrors.subscriptionKeyPrimary = 'Subscription Key (Primary) is required.'
    }
    if (!expireDate || isNaN(expireDate.getTime())) {
      newErrors.expireDate = 'Expiration Date must be a valid date.'
    }
    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const resetForm = () => {
    setForm({
      apiKey: '',
      subscriptionKeyPrimary: '',
      subscriptionKeySecondary: '',
      clientId: '',
    })
    setExpireDate(addOneYear(new Date()))
    setErrors({})
    setSubmitError(null)
    setIsSubmitting(false)
  }

  const handleOpenChange = (open: boolean) => {
    if (!open) resetForm()
    onOpenChange(open)
  }

  const handleSubmit = async () => {
    setSubmitError(null)
    if (!validate()) return

    setIsSubmitting(true)
    try {
      const result = await (dispatch as (action: unknown) => Promise<{ ok: boolean; error?: string }>)(
        createManualCredentialThunk(token, {
          apiKey: form.apiKey.trim(),
          subscriptionKeyPrimary: form.subscriptionKeyPrimary.trim(),
          subscriptionKeySecondary: form.subscriptionKeySecondary.trim() || undefined,
          clientId: form.clientId.trim() || undefined,
          expireDate: (expireDate ?? defaultExpireDate).toISOString(),
          nickname: generateNickname(),
        })
      )
      if (!result.ok) {
        setSubmitError(result.error ?? 'Failed to save credential.')
        return
      }
      resetForm()
      onSuccess()
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        className="p-0 gap-0 flex flex-col overflow-hidden sm:max-w-[520px] max-sm:fixed max-sm:bottom-0 max-sm:left-0 max-sm:right-0 max-sm:rounded-t-2xl max-sm:rounded-b-none max-sm:max-w-full max-sm:translate-y-0 max-sm:top-auto"
        aria-labelledby="manual-credential-modal-title"
        data-testid="manual-credential-paste-modal"
      >
        {/* ── Header — same chrome as HarvestModal ── */}
        <DialogHeader className="px-5 pt-5 pb-0 flex-shrink-0">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2.5">
              {/* TCS logo mark */}
              <div
                className="w-8 h-8 rounded-[8px] bg-accent-background border border-[#BFDBFE] flex items-center justify-center flex-shrink-0"
                aria-hidden="true"
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.8"
                  className="text-accent"
                  aria-hidden="true"
                >
                  <rect x="3" y="3" width="7" height="7" rx="1" />
                  <rect x="14" y="3" width="7" height="7" rx="1" />
                  <rect x="3" y="14" width="7" height="7" rx="1" />
                  <rect x="14" y="14" width="7" height="7" rx="1" />
                </svg>
              </div>
              <ChevronRight size={14} className="text-muted-foreground" aria-hidden="true" />
              {/* CampMinder logo mark */}
              <div
                className="w-8 h-8 rounded-[8px] bg-[#F0FDF4] border border-[#DCFCE7] flex items-center justify-center flex-shrink-0"
                aria-hidden="true"
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.8"
                  className="text-[#16A34A]"
                  aria-hidden="true"
                >
                  <path d="M3 20L12 4l9 16H3z" />
                </svg>
              </div>
            </div>
            <DialogTitle
              id="manual-credential-modal-title"
              className="text-sm font-semibold text-primary"
            >
              Paste your CampMinder API key
            </DialogTitle>
            <Button
              variant="ghost"
              size="icon"
              className="w-8 h-8 rounded-lg border border-border bg-muted hover:bg-destructive-foreground hover:text-destructive hover:border-destructive/30 transition-colors"
              aria-label="Close"
              onClick={() => handleOpenChange(false)}
            >
              <X size={15} aria-hidden="true" />
            </Button>
          </div>
        </DialogHeader>

        {/* ── Body ── */}
        <div className="flex-1 overflow-y-auto px-6 pt-5 pb-6 flex flex-col gap-5">
          {/* Subtitle */}
          <p className="text-[13px] text-secondary leading-relaxed">
            Find these in your CampMinder admin under{' '}
            <strong className="text-primary">Tools &rarr; API Access</strong>. Subscription Keys
            are listed at the bottom of the page.
          </p>

          {/* Form fields */}
          <div className="flex flex-col gap-4">
            <FieldRow
              id="apiKey"
              label="API Key"
              description="API key generated in CampMinder for accessing their APIs."
              placeholder="e.g. x0xxx0x0-xx00-0x0x-0xx0-x0x0xx0xx0x0"
              value={form.apiKey}
              onChange={setField('apiKey')}
              error={errors.apiKey}
              required
            />

            <FieldRow
              id="subscriptionKeyPrimary"
              label="Subscription Key (Primary)"
              description="Primary API subscription key associated with your CampMinder account."
              placeholder="e.g. x0xxx0x0-xx00-0x0x-0xx0-x0x0xx0xx0x0"
              value={form.subscriptionKeyPrimary}
              onChange={setField('subscriptionKeyPrimary')}
              error={errors.subscriptionKeyPrimary}
              required
            />

            <FieldRow
              id="subscriptionKeySecondary"
              label="Subscription Key (Secondary)"
              description="Secondary API subscription key. Optional — leave blank if you only have one."
              placeholder="e.g. x0xxx0x0-xx00-0x0x-0xx0-x0x0xx0xx0x0"
              value={form.subscriptionKeySecondary}
              onChange={setField('subscriptionKeySecondary')}
            />

            <FieldRow
              id="clientId"
              label="Client ID"
              description="CampMinder camp/client identifier. Optional — numeric string."
              placeholder="e.g. 228"
              value={form.clientId}
              onChange={setField('clientId')}
            />

            {/* Expiration Date */}
            <div className="flex flex-col gap-1">
              <Label className="text-sm font-semibold text-primary">
                Expiration Date
              </Label>
              <p className="text-xs text-muted-foreground leading-relaxed">
                Expiration date of the API key. Defaults to one year from today.
              </p>
              <DatePicker
                date={expireDate}
                setDate={setExpireDate}
                className="w-full"
              />
              {errors.expireDate && (
                <p role="alert" className="text-xs text-destructive font-medium">
                  {errors.expireDate}
                </p>
              )}
            </div>
          </div>

          {/* Submit error */}
          {submitError && (
            <p
              role="alert"
              data-testid="manual-credential-submit-error"
              className="text-sm text-destructive font-medium"
            >
              {submitError}
            </p>
          )}

          {/* Actions */}
          <div className="flex flex-col gap-3 mt-1">
            <Button
              type="button"
              className="w-full font-semibold"
              onClick={handleSubmit}
              disabled={isSubmitting}
              aria-label="Save credential"
            >
              {isSubmitting ? 'Saving...' : 'Save credential'}
            </Button>
            <button
              type="button"
              className="text-center text-sm text-secondary hover:text-accent hover:underline transition-colors"
              onClick={() => handleOpenChange(false)}
              aria-label="Cancel and close"
            >
              Cancel
            </button>
          </div>

          {/* Trust footer */}
          <div
            className="flex items-center gap-2 p-2.5 bg-muted border border-border rounded-lg"
            role="note"
            aria-label="Encryption notice"
          >
            <ShieldCheck size={14} className="text-success flex-shrink-0" aria-hidden="true" />
            <p className="text-xs text-secondary">TCS encrypts these values at rest.</p>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}
